import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from './AuthContext';

const FeedbackContext = createContext();

export const useFeedback = () => {
    const context = useContext(FeedbackContext);
    if (!context) {
        throw new Error('useFeedback deve ser usado dentro de um FeedbackProvider');
    }
    return context;
};

export const FeedbackProvider = ({ children }) => {
    const { user } = useAuth();
    const [feedbacks, setFeedbacks] = useState([]);
    const [loading, setLoading] = useState(false);

    // Real-time sync with Supabase
    useEffect(() => {
        setLoading(true);
        const fetchFeedbacks = async () => {
            const { data, error } = await supabase
                .from('feedbacks')
                .select('*')
                .order('id', { ascending: false });

            if (error) {
                console.error("Error fetching feedbacks:", error);
            } else {
                setFeedbacks(data || []);
            }
            setLoading(false);
        };

        fetchFeedbacks();

        const channel = supabase.channel('feedbacks-changes')
            .on('postgres_changes', { event: '*', schema: 'public', table: 'feedbacks' }, fetchFeedbacks)
            .subscribe();

        return () => {
            supabase.removeChannel(channel);
        };
    }, []);

    const addFeedback = async (data) => {
        setLoading(true);

        const id = Date.now().toString();
        const newFeedback = {
            id,
            type: data.type, // 'bug', 'error', 'suggestion'
            message: data.message,
            screenshot: data.screenshot || null, // Futuro
            path: window.location.pathname,
            url: window.location.href,
            user_agent: navigator.userAgent,
            status: 'open', // 'open', 'read', 'resolved'
            created_at: new Date().toISOString(),
            user: user ? {
                id: user.id || user.discordId,
                name: user.name || user.username,
                role: user.accessLevel || 'user',
                discordId: user.discordId
            } : {
                name: 'Anônimo',
                role: 'guest'
            }
        };

        try {
            const { error } = await supabase.from('feedbacks').insert(newFeedback);
            if (error) throw error;
            setLoading(false);
            return { success: true };
        } catch (error) {
            console.error("Error adding feedback:", error);
            setLoading(false);
            return { success: false, error };
        }
    };

    const markAsResolved = async (id) => {
        console.log(`[FeedbackContext] Attempting to resolve feedback ID: ${id} (Type: ${typeof id})`);
        try {
            const { data, error } = await supabase
                .from('feedbacks')
                .update({
                    status: 'resolved',
                    resolved_at: new Date().toISOString()
                })
                .eq('id', id)
                .select(); // Select to confirm update matches a row

            if (error) {
                console.error("[FeedbackContext] Supabase Update Error:", error);
                throw error;
            }

            if (!data || data.length === 0) {
                console.warn("[FeedbackContext] No rows were updated. ID mismatch?");
                return { success: false, error: "Feedback não encontrado ou já resolvido." };
            }

            return { success: true };
        } catch (error) {
            console.error("Error resolving feedback:", error);
            return { success: false, error: error.message };
        }
    };

    const deleteFeedback = async (id) => {
        try {
            const { error } = await supabase.from('feedbacks').delete().eq('id', id);
            if (error) throw error;
            return { success: true };
        } catch (error) {
            console.error("Error deleting feedback:", error);
            return { success: false };
        }
    };

    const refreshFeedbacks = async () => {
        setLoading(true);
        const { data, error } = await supabase
            .from('feedbacks')
            .select('*')
            .order('id', { ascending: false });

        if (error) {
            console.error("Error fetching feedbacks:", error);
        } else {
            setFeedbacks(data || []);
        }
        setLoading(false);
    };

    return (
        <FeedbackContext.Provider value={{
            feedbacks,
            loading,
            addFeedback,
            markAsResolved,
            deleteFeedback,
            refreshFeedbacks
        }}>
            {children}
        </FeedbackContext.Provider>
    );
};
